import Heading from '@atlaskit/heading/heading';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';
import {
	type AnyFrontendStage,
	type AnyProps,
	type NoProps,
	type PlacementBrowserConfig,
	type PlacementMessageFrontendFactory,
	type PlacementRenderer,
} from '@post-office/message-maker-browser/types';
import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';
import { usePlacementApi } from '@post-office/use-placement-api';
import { FormattedMessage } from 'react-intl';

type MessageTemplateContext = {
	message: string;
	link?: {
		href: string;
		text: string;
	};
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'sample-placement',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const createPlacementFrontendFactory = createPlacementFrontend(placementConfig);

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createPlacementFrontendFactory((Messages) => () => {
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<MessageTemplateContext>>;
	}>('/');

	if (error) {
		throw error;
	}

	return (
		<Stack space="space.150">
			<Heading as="h1" size="medium">
				<FormattedMessage
					id="post-office.sample-placement.main-heading.no-translate"
					defaultMessage="Welcome to Placements v2"
					description="Example welcome message for Placements v2"
				/>
			</Heading>
			{!isLoading && (
				<Stack as="ul" space="space.200">
					{data.messages.map((props) => (
						<Inline as="li" key={props.messageInstanceId}>
							<Messages.render {...props} />
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	);
});

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = createPlacementFrontendFactory;
